/* eslint-disable complexity */
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Textbox from 'carbon-react/lib/components/textbox';
import { MultiSelect, Option, Select } from 'carbon-react/lib/components/select';

import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';
import { COA_GROUP_FORM_FIELD_IDS } from './constants';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { AccountCategory } from '../../../../forecast/prediction-builder/types';
import { COADisplayAccount } from '../../../../chart-of-accounts/types';

export interface TemplateFormScreenProps {
  error?: string;
  validationErrors?: Record<string, { id: string; message: string }>;
  groupName: string;
  onGroupNameChange: (value: string) => void;
  onClose: () => void;
  onCreate: () => void;
  onBlur?: () => void;
  accountOptions: COADisplayAccount[];
  setSelectedAccounts: (accounts: COADisplayAccount[]) => void;
  selectedAccounts: COADisplayAccount[];
  category?: string;
  setSelectedCategory: (category: string) => void;
  revalidate: () => void;
  categories: string[];
  isEditMode: boolean;
}

export const GroupFormScreen = ({
  error,
  validationErrors = {},
  groupName,
  onGroupNameChange,
  onClose,
  onCreate,
  onBlur,
  setSelectedAccounts,
  revalidate,
  selectedAccounts,
  categories,
  category,
  setSelectedCategory,
  accountOptions,
  isEditMode
}: TemplateFormScreenProps) => {
  const intl = useIntl();

  const handleAccountsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!accountOptions) {
      return;
    }

    const newAccountIds = (event.target.value as unknown) as string[];
    const idSet = new Set(newAccountIds);
    const filteredAccounts = accountOptions.filter(obj => idSet.has(obj.id));

    setSelectedAccounts(filteredAccounts);
  };

  const handleCategoryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setSelectedCategory(event.target.value),
    [setSelectedCategory]
  );

  const { accountCategoryNames } = useLocalisedConstantsContext();

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={onCreate}
      createText={
        isEditMode ? (
          <FormattedMessage id="common.save" defaultMessage="Save" />
        ) : (
          <FormattedMessage id="common.create" defaultMessage="Create" />
        )
      }
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={3}>
          <Textbox
            label={intl.formatMessage({
              id: 'consolidation.group_name',
              defaultMessage: 'Group name'
            })}
            name={COA_GROUP_FORM_FIELD_IDS.GROUP_NAME}
            id={COA_GROUP_FORM_FIELD_IDS.GROUP_NAME}
            value={groupName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onGroupNameChange(e.target.value)}
            onBlur={onBlur}
            error={validationErrors.name?.message}
            required
          />
        </Box>
        <Box mb={3}>
          <Select
            disabled={isEditMode}
            required
            value={category ?? ''}
            autoComplete="off"
            label={intl.formatMessage({
              id: 'predict.category_to_add_group_to',
              defaultMessage: 'Category to add group to'
            })}
            onChange={handleCategoryChange}
            placeholder={intl.formatMessage({ id: 'common.predict.please_select', defaultMessage: 'Please select...' })}
            id={COA_GROUP_FORM_FIELD_IDS.CATEGORY}
            name={COA_GROUP_FORM_FIELD_IDS.CATEGORY}
            error={validationErrors.category?.message}
            onBlur={onBlur}
          >
            {categories.map(c => (
              <Option key={c} value={c} text={accountCategoryNames[c as AccountCategory]} />
            ))}
          </Select>
        </Box>
        {category && (
          <Box mb={3}>
            <MultiSelect
              label={intl.formatMessage({
                id: 'predict.select_accounts_to_add_to_group',
                defaultMessage: 'Select accounts to add to group'
              })}
              value={selectedAccounts.map(acc => acc.id)}
              onChange={handleAccountsChange}
              onBlur={revalidate}
              mb={2}
            >
              {accountOptions.map(acc => (
                <Option key={acc.id} text={acc.name} value={acc.id} />
              ))}
            </MultiSelect>
          </Box>
        )}
      </>
    </WizardScreen>
  );
};
